import {
	differenceBy,
	findIndex,
	isArray,
	pick,
	isFunction,
	isObject
} from 'lodash';

import gql from 'graphql-tag';

import {
	useCreateFishboneItemMutation,
	useUpdateFishboneItemMutation,
	useDeleteFishboneItemMutation,
	useUpdateFishboneItemScenarioMutation,
	useUpdateFishboneItemBranchMutation,
	useCreateFishboneBranchMutation,
	useUpdateFishboneBranchMutation,
	useDeleteFishboneBranchMutation,
	useUpdateFishboneBranchScenarioMutation,
	useCreateTaggingMutation,
	useDeleteTaggingMutation
} from 'graph/dist/generated';
import {
	getFieldFunctions,
	getFragmentFields,
	optimisticlyUpdateScenario,
	hideElementInOtherScenarios
} from 'utils/graphql';

const BRANCH_SCENARIO_FIELDS = ['weight', 'enabled'] as const;
const BRANCH_BASELINE_FIELDS = ['name', 'description', 'startOn', 'endOn', 'evaluatedAt', 'responsible', 'icon', 'position'] as const;
const ITEM_SCENARIO_FIELDS = ['trend', 'completion', 'weight', 'enabled'] as const;
const ITEM_BASELINE_FIELDS = ['name', 'description', 'startOn', 'endOn', 'reference', 'evaluatedAt', 'responsible', 'icon', 'position'] as const;

const formatDateField = (obj: Record<string, any>, field: string) => {
	try {
		if (obj[field] && typeof obj[field].format === 'function') {
			obj[field] = obj[field].format('YYYY-MM-DD');
		}
	} catch (e) { /* noop */ }
};

export const useFishboneController = () => {
	const [createFishboneItem] = useCreateFishboneItemMutation();
	const [updateFishboneItem] = useUpdateFishboneItemMutation();
	const [deleteFishboneItem] = useDeleteFishboneItemMutation();
	const [updateFishboneItemBranch] = useUpdateFishboneItemBranchMutation();
	const [updateFishboneItemScenario] = useUpdateFishboneItemScenarioMutation();

	const [createFishboneBranch] = useCreateFishboneBranchMutation();
	const [updateFishboneBranch] = useUpdateFishboneBranchMutation();
	const [deleteFishboneBranch] = useDeleteFishboneBranchMutation();
	const [updateFishboneBranchScenario] = useUpdateFishboneBranchScenarioMutation();

	const [createTagging] = useCreateTaggingMutation();
	const [deleteTagging] = useDeleteTaggingMutation();

	const updateTaggings = async (entity: any, tagIds: string[], type: 'fishbone_branch' | 'fishbone_item', cacheTypename: string, projectId: string) => {
		if (!tagIds || !isArray(tagIds)) return;
		const oldTaggings = entity.taggings || [];
		const newTaggings = tagIds.map((tagId) => ({ tagId, objectId: entity.id }));
		const toDelete = differenceBy(oldTaggings, newTaggings, 'tagId');
		const toAdd = differenceBy(newTaggings, oldTaggings, 'tagId');
		for (const tagging of toDelete) {
			await deleteTagging({
				variables: { id: tagging.id },
				update(cache, { data: { deleteTagging } }: any) {
					cache.modify({
						id: `${cacheTypename}:${entity.id}`,
						fields: {
							taggings: (taggings = []) =>
								taggings.filter((t: any) => t.__ref !== `Tagging:${deleteTagging.id}`)
						}
					});
				}
			});
		}
		for (const tagging of toAdd) {
			const variables = { projectId, ...tagging, type };
			await createTagging({
				variables,
				update(cache, { data: { createTagging } }: any) {
					cache.modify({
						id: `${cacheTypename}:${entity.id}`,
						fields: {
							taggings: (taggings = []) => {
								const newRef = cache.writeFragment({
									id: 'Tagging:' + createTagging.id,
									data: createTagging,
									fragment: gql`fragment TF on Tagging { ${getFragmentFields(variables)} }`
								});
								return [...taggings, newRef];
							}
						}
					});
				}
			});
		}
	};

	// ─── Branch operations ───────────────────────────────────────────────

	const handleCreateFishboneBranch = async (
		projectId: string,
		values: any,
		scenario: any = null,
		scenarios: any[] = []
	) => {
		const variables = {
			projectId,
			name: values.name,
			weight: values.weight || 1,
			description: values.description || '',
			responsible: values.responsible,
			startOn: values.startOn && isObject(values.startOn)
				? values.startOn.format('YYYY-MM-DD')
				: values.startOn,
			endOn: values.endOn && isObject(values.endOn)
				? values.endOn.format('YYYY-MM-DD')
				: values.endOn,
			icon: values.icon || 'faCrosshairs',
			enabled: scenario ? false : true
		};

		const result = await createFishboneBranch({
			variables,
			update(cache, { data: { createFishboneBranch } }: any) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						fishboneBranchs(existing = [], { toReference }) {
							return [
								...existing,
								toReference(`FishboneBranch:${createFishboneBranch.id}`)
							];
						}
					}
				});
			},
			optimisticResponse: {
				createFishboneBranch: {
					id: 'temp-id',
					__typename: 'FishboneBranch',
					...variables,
					items: [],
					scenarios: [],
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
					evaluatedAt: '',
					reference: ''
				}
			}
		});

		if (scenario && result.data?.createFishboneBranch?.id) {
			await optimisticlyUpdateScenario(
				result.data.createFishboneBranch,
				'FishboneBranch',
				scenario.id,
				{ enabled: true },
				updateFishboneBranchScenario
			);
		}

		if (result.data?.createFishboneBranch?.id && scenarios.length > 0) {
			await hideElementInOtherScenarios(
				result.data.createFishboneBranch,
				'FishboneBranch',
				scenarios,
				scenario,
				updateFishboneBranchScenario
			);
		}

		return result;
	};

	const handleUpdateFishboneBranch = async (
		id: string,
		branches: any[],
		scenario: any,
		values: any
	) => {
		const index = findIndex(branches, (s: any) => s.id === id);

		const baselineValues = pick(values, [...BRANCH_BASELINE_FIELDS]);
		const hasBaselineChanges = Object.keys(baselineValues).length > 0;

		const scenarioValuesFromForm = pick(values, [...BRANCH_SCENARIO_FIELDS]);
		const hasScenarioChanges = Object.keys(scenarioValuesFromForm).length > 0;

		if (hasBaselineChanges) {
			const newBranch = {
				...pick(branches[index], Object.keys(baselineValues)),
				...baselineValues
			};
			formatDateField(newBranch, 'startOn');
			formatDateField(newBranch, 'endOn');
			formatDateField(newBranch, 'evaluatedAt');

			await updateFishboneBranch({
				variables: { id, ...newBranch },
				update(cache) {
					cache.modify({
						id: `FishboneBranch:${id}`,
						fields: getFieldFunctions(baselineValues)
					});
				},
				optimisticResponse: { updateFishboneBranch: true }
			});
		}

		if (hasScenarioChanges) {
			if (scenario) {
				await optimisticlyUpdateScenario(
					branches[index],
					'FishboneBranch',
					scenario.id,
					scenarioValuesFromForm,
					updateFishboneBranchScenario
				);
			} else {
				await updateFishboneBranch({
					variables: { id, ...scenarioValuesFromForm },
					update(cache) {
						cache.modify({
							id: `FishboneBranch:${id}`,
							fields: getFieldFunctions(scenarioValuesFromForm)
						});
					},
					optimisticResponse: { updateFishboneBranch: true }
				});
			}
		}
	};

	// ─── Reordering (position manuelle, #1 P2) ───────────────────────────
	//
	// Strategie robuste face aux donnees legacy sans `position` :
	// 1. on part de la liste DEJA TRIEE telle qu'affichee (caller),
	// 2. on calcule une position canonique = index pour chaque noeud,
	// 3. on swappe l'element avec son voisin (haut/bas),
	// 4. on persiste UNIQUEMENT les deux positions qui changent.
	// Resultat : meme si les positions etaient nulles/identiques, on materialise
	// un ordre coherent au premier deplacement.
	const computeReorderUpdates = (
		orderedNodes: any[],
		id: string,
		direction: 'up' | 'down'
	): Array<{ id: string; position: number }> | null => {
		const index = orderedNodes.findIndex((n) => n.id === id);
		if (index < 0) return null;
		const target = direction === 'up' ? index - 1 : index + 1;
		if (target < 0 || target >= orderedNodes.length) return null;

		// Positions canoniques = rang dans la liste affichee.
		const a = orderedNodes[index];
		const b = orderedNodes[target];
		return [
			{ id: a.id, position: target },
			{ id: b.id, position: index }
		];
	};

	const persistPosition = async (
		kind: 'branch' | 'item',
		id: string,
		position: number
	) => {
		const variables = { id, position };
		const cacheId =
			kind === 'branch' ? `FishboneBranch:${id}` : `FishboneItem:${id}`;
		const mutate = kind === 'branch' ? updateFishboneBranch : updateFishboneItem;
		const optimistic =
			kind === 'branch'
				? { updateFishboneBranch: true }
				: { updateFishboneItem: true };

		await mutate({
			variables,
			update(cache) {
				cache.modify({
					id: cacheId,
					fields: { position: () => position }
				});
			},
			optimisticResponse: optimistic as any
		});
	};

	const handleReorderFishboneBranch = async (
		orderedBranches: any[],
		id: string,
		direction: 'up' | 'down'
	) => {
		const updates = computeReorderUpdates(orderedBranches, id, direction);
		if (!updates) return;
		// En parallèle : toutes les MAJ optimistes du cache s'appliquent dans le
		// même tick -> le chart se réordonne d'un coup (pas gated par les round-trips).
		await Promise.all(updates.map((u) => persistPosition('branch', u.id, u.position)));
	};

	const handleReorderFishboneItem = async (
		orderedItems: any[],
		id: string,
		direction: 'up' | 'down'
	) => {
		const updates = computeReorderUpdates(orderedItems, id, direction);
		if (!updates) return;
		await Promise.all(updates.map((u) => persistPosition('item', u.id, u.position)));
	};

	// ─── Reordonnancement par drop a un index arbitraire (drag-and-drop) ─────
	//
	// `orderedNodes` est la liste DEJA TRIEE telle qu'affichee. On retire l'element
	// drague, on le reinsere a `targetIndex`, puis on reecrit la position = rang
	// pour CHAQUE noeud dont la position effective change. Robuste face aux donnees
	// legacy sans `position` : tout est materialise au premier drop.
	const computeMoveToIndexUpdates = (
		orderedNodes: any[],
		id: string,
		targetIndex: number
	): Array<{ id: string; position: number }> | null => {
		const fromIndex = orderedNodes.findIndex((n) => n.id === id);
		if (fromIndex < 0) return null;

		const without = orderedNodes.filter((n) => n.id !== id);
		const clamped = Math.max(0, Math.min(targetIndex, without.length));
		const moved = orderedNodes[fromIndex];
		const next = [...without.slice(0, clamped), moved, ...without.slice(clamped)];

		const updates: Array<{ id: string; position: number }> = [];
		next.forEach((n, index) => {
			// On ne persiste que ce qui change reellement (ou n'avait pas de position).
			if (n.position !== index) updates.push({ id: n.id, position: index });
		});
		return updates;
	};

	const handleMoveFishboneBranchToIndex = async (
		orderedBranches: any[],
		id: string,
		targetIndex: number
	) => {
		const updates = computeMoveToIndexUpdates(orderedBranches, id, targetIndex);
		if (!updates || updates.length === 0) return;
		// En parallèle : toutes les MAJ optimistes du cache s'appliquent dans le
		// même tick -> le chart se réordonne d'un coup (pas gated par les round-trips).
		await Promise.all(updates.map((u) => persistPosition('branch', u.id, u.position)));
	};

	const handleMoveFishboneItemToIndex = async (
		orderedItems: any[],
		id: string,
		targetIndex: number
	) => {
		const updates = computeMoveToIndexUpdates(orderedItems, id, targetIndex);
		if (!updates || updates.length === 0) return;
		await Promise.all(updates.map((u) => persistPosition('item', u.id, u.position)));
	};

	const handleDeleteFishboneBranch = async (id: string | undefined, projectId: string) => {
		if (id == null) return;

		await deleteFishboneBranch({
			variables: { id },
			update(cache) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						fishboneBranchs(existingRefs = [], { readField }) {
							return existingRefs.filter(
								(ref: string) => id !== readField('id', ref)
							);
						}
					}
				});
			},
			optimisticResponse: { deleteFishboneBranch: true }
		});
	};

	// ─── Item operations ─────────────────────────────────────────────────

	const handleCreateFishboneItem = async (
		fishboneBranchId: string,
		values: any,
		defaults: any = {},
		scenario: any = null,
		scenarios: any[] = []
	) => {
		const baseDefaults = {
			trend: '50',
			completion: 0,
			description: '',
			weight: 1
		};

		const appliedDefaults = { ...baseDefaults, ...defaults };

		let evaluatedAt = values.evaluatedAt || appliedDefaults.evaluatedAt;
		evaluatedAt =
			!values.evaluatedAt || !isFunction(values.evaluatedAt?.toISOString)
				? ''
				: new Date(values.evaluatedAt).toISOString();

		const variables = {
			fishboneBranchId,
			name: values.name,
			weight: values.weight || 1,
			trend: values.trend || appliedDefaults.trend,
			completion: values.completion || appliedDefaults.completion,
			description: values.description || appliedDefaults.description,
			startOn:
				values.startOn && isObject(values.startOn)
					? values.startOn.format('YYYY-MM-DD')
					: values.startOn,
			endOn:
				values.endOn && isObject(values.endOn)
					? values.endOn.format('YYYY-MM-DD')
					: values.endOn,
			reference: values.reference || '',
			evaluatedAt,
			responsible: values.responsible || '',
			enabled: scenario ? false : true
		};

		const result = await createFishboneItem({
			variables,
			update(cache, { data: { createFishboneItem } }: any) {
				cache.modify({
					id: `FishboneBranch:${fishboneBranchId}`,
					fields: {
						items(existing = [], { toReference }) {
							return [
								...existing,
								toReference(`FishboneItem:${createFishboneItem.id}`)
							];
						}
					}
				});
			},
			optimisticResponse: {
				createFishboneItem: {
					id: 'temp-id',
					__typename: 'FishboneItem',
					...variables,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
					scenarios: []
				}
			}
		});

		if (scenario && result.data?.createFishboneItem?.id) {
			await optimisticlyUpdateScenario(
				result.data.createFishboneItem,
				'FishboneItem',
				scenario.id,
				{
					trend: variables.trend || '50',
					completion: variables.completion || 0,
					weight: variables.weight || 1,
					enabled: true
				},
				updateFishboneItemScenario
			);
		}

		if (result.data?.createFishboneItem?.id && scenarios.length > 0) {
			await hideElementInOtherScenarios(
				result.data.createFishboneItem,
				'FishboneItem',
				scenarios,
				scenario,
				updateFishboneItemScenario
			);
		}

		return result;
	};

	const handleUpdateFishboneItemBranch = async (
		id: string,
		newBranchId: string,
		oldBranchId: string
	) => {
		const values = { fishboneBranchId: newBranchId };

		await updateFishboneItemBranch({
			variables: { id, fishboneBranchId: newBranchId },
			update(cache) {
				cache.modify({
					id: `FishboneItem:${id}`,
					fields: getFieldFunctions(values)
				});
				cache.modify({
					id: `FishboneBranch:${oldBranchId}`,
					fields: {
						items(existingRefs = [], { readField }) {
							return existingRefs.filter(
								(ref: string) => readField('id', ref) !== id
							);
						}
					}
				});
				cache.modify({
					id: `FishboneBranch:${newBranchId}`,
					fields: {
						items(existingRefs = [], { toReference }) {
							return [
								...existingRefs,
								toReference(`FishboneItem:${id}`)
							];
						}
					}
				});
			}
		});
	};

	const handleUpdateFishboneItem = async (id: string, items: any[], scenario: any, values: any) => {
		const index = findIndex(items, (s: any) => s.id === id);

		const baselineValues = pick(values, [...ITEM_BASELINE_FIELDS]);
		const hasBaselineChanges = Object.keys(baselineValues).length > 0;

		const scenarioValuesFromForm = pick(values, [...ITEM_SCENARIO_FIELDS]);
		const hasScenarioChanges = Object.keys(scenarioValuesFromForm).length > 0;

		if (hasBaselineChanges) {
			const baselineItem: any = {
				...pick(items[index], Object.keys(baselineValues)),
				...baselineValues
			};

			if (baselineValues.evaluatedAt !== undefined)
				baselineItem.evaluatedAt =
					!baselineItem.evaluatedAt ||
					!isFunction(baselineItem.evaluatedAt?.toISOString)
						? ''
						: baselineItem.evaluatedAt.toISOString();

			await updateFishboneItem({
				variables: { id, ...baselineItem },
				update(cache) {
					cache.modify({
						id: `FishboneItem:${id}`,
						fields: getFieldFunctions(baselineValues)
					});
				},
				optimisticResponse: { updateFishboneItem: true }
			});
		}

		if (hasScenarioChanges) {
			if (scenario) {
				await optimisticlyUpdateScenario(
					items[index],
					'FishboneItem',
					scenario.id,
					scenarioValuesFromForm,
					updateFishboneItemScenario
				);
			} else {
				await updateFishboneItem({
					variables: { id, ...scenarioValuesFromForm },
					update(cache) {
						cache.modify({
							id: `FishboneItem:${id}`,
							fields: getFieldFunctions(scenarioValuesFromForm)
						});
					},
					optimisticResponse: { updateFishboneItem: true }
				});
			}
		}
	};

	const handleDeleteFishboneItem = async (
		fishboneBranchId: string,
		id: string | undefined
	) => {
		if (id == null) return;

		await deleteFishboneItem({
			variables: { id },
			update(cache) {
				cache.modify({
					id: `FishboneBranch:${fishboneBranchId}`,
					fields: {
						items(existingRefs = [], { readField }) {
							return existingRefs.filter(
								(ref: string) => id !== readField('id', ref)
							);
						}
					}
				});
			},
			optimisticResponse: { deleteFishboneItem: true }
		});
	};

	return {
		createFishboneBranch: handleCreateFishboneBranch,
		updateFishboneBranch: handleUpdateFishboneBranch,
		deleteFishboneBranch: handleDeleteFishboneBranch,
		reorderFishboneBranch: handleReorderFishboneBranch,
		moveFishboneBranchToIndex: handleMoveFishboneBranchToIndex,

		createFishboneItem: handleCreateFishboneItem,
		updateFishboneItem: handleUpdateFishboneItem,
		updateFishboneItemBranch: handleUpdateFishboneItemBranch,
		deleteFishboneItem: handleDeleteFishboneItem,
		reorderFishboneItem: handleReorderFishboneItem,
		moveFishboneItemToIndex: handleMoveFishboneItemToIndex,

		updateBranchTaggings: (branch: any, tagIds: string[], projectId: string) =>
			updateTaggings(branch, tagIds, 'fishbone_branch', 'FishboneBranch', projectId),
		updateItemTaggings: (item: any, tagIds: string[], projectId: string) =>
			updateTaggings(item, tagIds, 'fishbone_item', 'FishboneItem', projectId),
	};
};
